import React, { useEffect, useState } from 'react'
import { APP_SETTINGS } from '../../../constants/settings'
import { getReceiverState, updateReceiverState } from '../../../core/state/ReceiverStore'
import { broadcastToSenders } from '../../../core/cast/CastReceiverManager'
import { MessageType } from '../../../constants/namespaces'

// Super simplified version with large buttons
const LoadingScreen: React.FC = () => {
  const [receiverMessages, setReceiverMessages] = useState<string[]>([]);
  const [lastSenderId, setLastSenderId] = useState<string | null>(null);

  // Set up debug info update
  useEffect(() => {
    // Monitor console logs
    const originalConsoleLog = console.log;
    console.log = function(...args) {
      // Special case to capture sender IDs from the logs
      const message = args.map(a => 
        typeof a === 'object' ? JSON.stringify(a).substring(0, 100) : String(a)
      ).join(' ');
      
      if (message.includes('Sender connected')) {
        const match = message.match(/senderId: "(.*?)"/);
        if (match && match[1]) {
          setLastSenderId(match[1]);
          originalConsoleLog.apply(console, ['Captured sender ID:', match[1]]);
        }
      }
      
      if (message.includes('message') || message.includes('recipe') || 
          message.includes('cast') || message.includes('sender')) {
        setReceiverMessages(prev => [
          `${new Date().toLocaleTimeString()}: ${message.substring(0, 200)}`,
          ...prev.slice(0, 5)
        ]);
      }
      
      originalConsoleLog.apply(console, args);
    };
    
    return () => {
      console.log = originalConsoleLog;
    };
  }, []);

  // Function to send a test message
  const handleTestConnection = () => {
    console.log('➡️ TEST CONNECTION function triggered');
    
    try {
      // Send a pong to all senders to test communication
      broadcastToSenders({
        type: MessageType.PONG,
        timestamp: Date.now(),
        testMessage: 'Hello from receiver'
      });
      
      console.log('Sent test PONG message to all senders');
      
      // Also try sending this as a direct button event
      document.dispatchEvent(new CustomEvent('test-connection-button-pressed'));
      console.log('Dispatched custom test-connection event');
    } catch (error) {
      console.error('Error sending test message:', error);
    }
  };
  
  // Create a mock recipe and load it
  const handleLoadTestRecipe = () => {
    console.log('🔍 Load Test Recipe function triggered');
    console.log('⚠️ DIRECTLY CALLING handleLoadTestRecipe function');
    
    try {
      // Create a recipe that matches the expected format in our Layout component
      const testRecipe = {
        id: 'debug-recipe-' + Date.now(),
        title: 'Debug Test Recipe',
        description: 'A test recipe for debugging',
        servings: 4,
        prepTimeMinutes: 10,
        cookTimeMinutes: 20,
        totalTimeMinutes: 30,
        difficulty: 'easy',
        imageUrl: '',
        tags: ['test', 'debug'],
        ingredients: [
          {
            id: 'ing-1',
            name: 'Ingredient 1',
            quantity: 2,
            unit: 'cups'
          },
          {
            id: 'ing-2',
            name: 'Ingredient 2',
            quantity: 1,
            unit: 'tbsp'
          },
          {
            id: 'ing-3',
            name: 'Ingredient 3',
            quantity: 3
          }
        ],
        steps: [
          {
            id: 'step-1',
            instructions: 'This is step 1 of the test recipe. Tap buttons to navigate.',
            imageUrl: '',
            durationMinutes: 5
          },
          {
            id: 'step-2',
            instructions: 'This is step 2 of the test recipe. You should be able to navigate between steps.',
            imageUrl: '',
            durationMinutes: 10
          }
        ]
      };
      
      console.log('Created test recipe object:', { id: testRecipe.id, title: testRecipe.title });
      
      // Also try sending this as a direct button event
      document.dispatchEvent(new CustomEvent('load-recipe-button-pressed'));
      console.log('Dispatched custom load-recipe event');
      
      // First directly update the state with our test recipe (old implementation)
      updateReceiverState({
        recipe: testRecipe,
        currentStepIndex: 0,
        showingIngredients: false,
        timerActive: false,
        servings: 4,
        lastUpdated: new Date().toISOString()
      });
      
      // Then check if it was set
      const state = getReceiverState();
      console.log('✅ Receiver state after update:', { 
        hasRecipe: !!state.recipe, 
        recipeId: state.recipe?.id,
        recipeTitle: state.recipe?.title
      });
      
      // Also try calling the message handler directly
      try {
        const msgHandler = require('../../../core/cast/MessageHandler');
        console.log('Found message handler module');
        if (msgHandler.handleSenderMessage) {
          console.log('Calling handleSenderMessage directly with test recipe');
          msgHandler.handleSenderMessage({
            senderId: 'test-button-' + Date.now(),
            data: {
              type: 'LOAD_RECIPE',
              recipe: testRecipe,
              timestamp: Date.now()
            }
          });
        }
      } catch (e) {
        console.error('Error in direct message handler call:', e);
      }
      
      console.log('Loaded mock recipe for debugging');
    } catch (error) {
      console.error('Error loading mock recipe:', error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-4">
      {/* Main Content */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-6 text-green-500">
          {APP_SETTINGS.APP_NAME}
        </h1>
        
        <p className="text-2xl mb-6">Waiting for recipe...</p>
        
        <p className="text-gray-400 mb-8">
          Cast a recipe from your device to get started
        </p>
      </div>
      
      {/* Large, simple buttons */}
      <div className="w-full max-w-md space-y-4 mb-8">
        <button
          type="button"
          className="w-full py-6 px-4 text-xl font-bold bg-blue-700 hover:bg-blue-600 active:bg-blue-800 rounded-lg text-white"
          onTouchStart={() => console.log('Touch start on TEST CONNECTION button')}
          onTouchEnd={() => handleTestConnection()}
          onClick={() => handleTestConnection()}
          onMouseDown={() => console.log('Mouse down on TEST CONNECTION button')}
          onMouseUp={() => console.log('Mouse up on TEST CONNECTION button')}
        >
          TEST CONNECTION
        </button>
        
        <button
          type="button"
          className="w-full py-6 px-4 text-xl font-bold bg-green-700 hover:bg-green-600 active:bg-green-800 rounded-lg text-white"
          onTouchStart={() => console.log('Touch start on LOAD TEST RECIPE button')}
          onTouchEnd={() => handleLoadTestRecipe()}
          onClick={() => handleLoadTestRecipe()}
          onMouseDown={() => console.log('Mouse down on LOAD TEST RECIPE button')}
          onMouseUp={() => console.log('Mouse up on LOAD TEST RECIPE button')}
        >
          LOAD TEST RECIPE
        </button>
      </div>
      
      {/* Simple log display */}
      <div className="w-full max-w-md bg-black bg-opacity-80 rounded-lg p-4 mb-4">
        <h3 className="font-bold mb-2">Status:</h3>
        <p>Recipe loaded: {getReceiverState().recipe ? 'Yes' : 'No'}</p>
        <p>Last Sender ID: {lastSenderId ? 
          (lastSenderId.length > 20 ? 
            lastSenderId.substring(0, 20) + '...' : 
            lastSenderId) : 
          'None'}
        </p>
      </div>
      
      {/* Last few messages */}
      <div className="w-full max-w-md bg-black bg-opacity-80 rounded-lg p-4">
        <h3 className="font-bold mb-2">Recent Messages:</h3>
        {receiverMessages.length === 0 ? (
          <p>No messages logged yet</p>
        ) : (
          <ul>
            {receiverMessages.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        )}
      </div>
      
      <div className="mt-8 text-gray-500 text-sm">
        <p>Powered by RecipeCast</p>
      </div>
    </div>
  );
}

export default LoadingScreen
